import React, { useState, useEffect, useMemo, useRef } from 'react';
import DirectSaleListInstallments from '../DirectSaleListInstallments';
import {
  QUERY_ACCOUNT_RECEIVABLE,
  useAccountReceivableFinancialPendingBySale,
  useAccountReceivableListBySale,
  useGenerateAccountReceivableBySale,
} from '@/core/domain/financial/AccountReceivable/AccountReceivable.hooks';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import { useRouteMatch } from 'react-router-dom';

import DirectSaleFinancialInstallments from '@/components/Movements/DirectSale/DirectSaleFinancial/DirectSaleFinancialInstallments';

import {
  Button,
  Col,
  Divider,
  Empty,
  Form,
  Popover,
  Row,
  Skeleton,
  Tabs,
  message,
} from 'antd';
import moment, { Moment } from 'moment';
import FinancialDirectSaleForm from '../FinancialDirectSaleForm';
import { useDirectSale } from '@/core/domain/sales/DirectSale/DirectSale.hooks';
import { usePaymentConditionMutate } from '@/core/domain/enterprise/PaymentCondition/PaymentCondition.hooks';
import { useDocumentTypeMutation } from '@/core/domain/enterprise/DocumentTypes/DocumentTypes.hooks';
import { useIntl } from 'react-intl';
import { AccountReceivableBySaleRequest } from '@/core/domain/financial/AccountReceivable/AccountReceivable.types';
import SituationFormField from '@/components/Movements/DirectSale/SituationFormField';
import DirectSaleFinancialStatistic from '../DirectSaleFinancialStatistic';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import ButtonBack from '@/components/_common/ButtonBack';
import { SaveButton } from '@/components/_common';
import { DirectSaleStatusEnum } from '@/core/domain/sales/DirectSale/DirectSale.types';
import { DateTimeFormat } from '@/utils/moment';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';
import {
  useCompany,
  useCompanySettings,
} from '@/core/domain/enterprise/Companies/Companies.hooks';
import StoneOrderCreateModal from '../../../../Stone/StoneOrderCreateModal';
import { useDisclosure, useWindowSize } from '@/hooks';
import { getInstallmentValues } from './getInstallmentValues';
import { StoneOrderOriginType } from '@/core/domain/stone/orders/orders.types';
import { useStoneAccountDetailbyCompany } from '@/core/domain/stone/stoneAccount/stoneAccount.hooks';
import StoneOrderProcessInfo from '@/components/Stone/StoneOrderProcessInfo';
import StoneDevicesSelectModal from '@/components/Stone/StoneDevicesSelectModal';
import { StoneDevice } from '@/core/domain/stone/devices/devices.types';
import ActionIcon from '@/components/_common/ActionIcon';
import { useQueryChanel } from '@/contexts/QueryChanelProvider';
import ModalLoadingOverlay from '@/components/_common/ModalLoadingOverlay';
import { useStoneDeviceList } from '@/core/domain/stone/devices/devices.hooks';
import { useStoneOrderHasOpenMutate } from '@/core/domain/stone/orders/orders.hooks';
import { DocumentTypePaymentType } from '@/core/domain/enterprise/DocumentTypes/DocumentTypes.types';
import TEF, { TefOnResultParams } from '@/components/TEF';
import { usePaymentTransactionCreate } from '@/core/domain/payment-gateway/transactions/transactions.hooks';
import {
  PaymentFinancialPendingStatus,
  TransactionAccreditor,
  TransactionClientType,
  TransactionEntityType,
  TransactionPaymentTypeByTef,
} from '@/core/domain/payment-gateway/transactions/transactions.types';
import FinancialTypeGenerate, {
  FinancialType,
} from '@/components/_common/FinancialTypeGenerate';
import TEFProcessFinancial, {
  TEFProcessFinancialRef,
} from '@/components/TEF/TEFProcessFinancial';

type TabItem = {
  key: string;
  label: string;
  children: React.ReactNode;
};

export interface DirectSaleFinancialFormValues {
  planAccountId: number;
  documentTypeId: number;
  paymentConditionId: number;
  valueTotal: number;
  increasePercent: number;
  increaseAmount: number;
  decreasePercent: number;
  decreaseAmount: number;
  costCenterId: number;
  authorizationNumber: string;
  bankAccountId: number;
}
export interface DirectSaleFinancialInstallmentList {
  description: string;
  documentNumber: string;
  discountIncrease?: number;
  rateValue?: number;
  realValue: number;
  documentTypeId?: number;
  installmentValue: number;
  dueDate: Moment;
  receivableValue: number;
}

const DirectSaleFinancialTabs: React.FC = () => {
  const intl = useIntl();
  const queryChanel = useQueryChanel();

  const paymentStatusRef = useRef<TEFProcessFinancialRef>(null);

  const { directSaleId } = useRouteMatch<{ directSaleId: string }>().params;
  const [form] = Form.useForm<DirectSaleFinancialFormValues>();

  const [loadingFinancial, setLoadingFinancial] = useState(false);

  const [financialType, setFinancialType] = useState<FinancialType>(
    FinancialType.Default,
  );
  const isStone = financialType === 'stone';

  const [stoneDevice, setStoneDevice] = useState<StoneDevice>();

  useEffect(() => {
    if (financialType !== 'stone') {
      form.setFieldValue('documentTypeId', undefined);
    }
  }, [financialType]);

  const [installments, setInstallments] = useState<
    DirectSaleFinancialInstallmentList[]
  >([]);

  const hasMax12InstallmentAndLower10 =
    installments.length <= 12 &&
    installments.some((item) => item.installmentValue < 0.1);

  const hasOver12InstallmentAndLower10 =
    installments.length > 12 &&
    installments.some((item) => item.installmentValue < 10);

  const generateFinancial = useGenerateAccountReceivableBySale();

  const [stoneModalOpened, stoneModalAction] = useDisclosure();

  const {
    data: accountsReceivableBySaleData,
    dataUpdatedAt: receivableDataUpdatedAt,
    fetchNextPage,
    hasNextPage,
    isLoading,
    refetch: InstallmentsRefetch,
  } = useAccountReceivableListBySale({
    order: 'DESC',
    saleId: Number(directSaleId),
  });

  const directSaleAccounts = flatInfiniteData(accountsReceivableBySaleData);

  const {
    data: directSale,
    isLoading: useDirectSaleLoading,
    dataUpdatedAt,
  } = useDirectSale(Number(directSaleId));

  const { data: company } = useCompany(directSale?.companyId);

  const directSaleFinancialPending = useAccountReceivableFinancialPendingBySale(
    { saleId: directSale?.id },
  );

  const stoneAccount = useStoneAccountDetailbyCompany(directSale?.companyId);

  const stoneDevices = useStoneDeviceList({
    companyId: directSale?.companyId,
    isActive: true,
  });

  const stoneHasOrderOpen = useStoneOrderHasOpenMutate();

  const totalValues = useMemo(() => {
    const financialPending = directSaleFinancialPending.data;

    return {
      totalDirectSale: financialPending?.totalAmount || 0,
      totalDirectSalaeAccounts: financialPending?.totalValueGenerated || 0,
      remainingAmount: financialPending?.totalValuePending || 0,
      totalIsEqual: financialPending?.totalValuePending === 0,
    };
  }, [directSaleFinancialPending.data]);

  const formValues = Form.useWatch([], form);

  useEffect(() => {
    if (
      totalValues &&
      (typeof formValues?.valueTotal !== 'number' ||
        formValues.valueTotal !== totalValues.remainingAmount)
    ) {
      form.setFieldValue('valueTotal', totalValues.remainingAmount);
    }
  }, [dataUpdatedAt, receivableDataUpdatedAt, totalValues]);

  const { data: paymentCondition, mutateAsync: paymentConditionMutate } =
    usePaymentConditionMutate();
  const documentTypeDetail = useDocumentTypeMutation();
  const { data: companySetting } = useCompanySettings(directSale?.companyId);

  const documentTypePaymentType = documentTypeDetail.data?.paymentType;

  const paymentConditionId = Form.useWatch('paymentConditionId', form);
  const documentTypeId = Form.useWatch('documentTypeId', form);
  const valueTotal = Form.useWatch('valueTotal', form);

  const installmentQuantity = Form.useWatch('installmentQuantity', form);
  const installmentDue = Form.useWatch('installmentDue', form);
  const installmentPeriod = Form.useWatch('installmentPeriod', form) as
    | number
    | undefined;

  const isCanceled = directSale?.status === DirectSaleStatusEnum.CANCELED;

  useEffect(() => {
    if (paymentConditionId) {
      paymentConditionMutate(paymentConditionId);
    }
  }, [paymentConditionId]);

  useEffect(() => {
    if (documentTypeId) {
      documentTypeDetail.mutateAsync(documentTypeId);
    } else {
      documentTypeDetail.reset();
    }
  }, [documentTypeId]);

  useEffect(() => {
    if (
      valueTotal &&
      (companySetting?.isInstallmentFree || paymentCondition) &&
      documentTypeId &&
      documentTypeDetail.data
    ) {
      const filledArray = new Array(
        companySetting?.isInstallmentFree
          ? installmentQuantity || 0
          : paymentCondition?.installments.length,
      ).fill(0);

      setInstallments(
        filledArray.reduce<DirectSaleFinancialInstallmentList[]>(
          (acc, _, i) => {
            let dueDate =
              companySetting?.isInstallmentFree && !isStone
                ? (installmentDue as Moment)
                : moment();

            if (
              !companySetting?.isInstallmentFree &&
              companySetting?.shouldUpdateDueDateBasedOnCreationDate
            ) {
              dueDate = moment(directSale?.saleDate);
            }

            const paymentType = documentTypeDetail.data.paymentType;

            const paymentTypeInterval =
              typeof paymentType !== 'undefined' && isStone
                ? paymentType === DocumentTypePaymentType.Credit
                  ? 30 * (i + 1)
                  : 1
                : undefined;

            const defaultInterval = companySetting?.isInstallmentFree
              ? (installmentPeriod || 0) * i
              : paymentCondition?.installments[i].numberOfDays;

            dueDate = dueDate
              ?.clone()
              .add(
                Number.isInteger(paymentTypeInterval)
                  ? paymentTypeInterval
                  : defaultInterval,
                'days',
              );

            const formIncreaseDecrease =
              ((formValues?.increaseAmount || 0) +
                (formValues?.decreaseAmount || 0) * -1) /
              filledArray.length;

            const {
              realValue,
              receivableValue,
              installmentValue,
              discountIncrease,
              tariff,
            } = getInstallmentValues({
              documentType: documentTypeDetail.data,
              index: i,
              installmentQuantity: filledArray.length,
              valueTotal,
              financialType,
              isInstallmentFree: !!companySetting?.isInstallmentFree,
              paymentCondition,
              increaseDecrease: formIncreaseDecrease,
            });

            return [
              ...acc,
              {
                description: `${i + 1}º Parcela - ${directSale?.saleCode}`,
                documentNumber: `${directSale?.saleCode}/${i + 1}`,
                documentTypeId: documentTypeId,
                discountIncrease,
                dueDate: dueDate ?? moment(),
                rateValue: tariff,
                realValue: realValue,
                installmentValue: installmentValue,
                receivableValue: receivableValue,
              },
            ];
          },
          [] as DirectSaleFinancialInstallmentList[],
        ),
      );
    } else {
      setInstallments([]);
    }
  }, [
    directSale,
    paymentCondition,
    documentTypeDetail.data,
    documentTypeId,
    valueTotal,
    installmentQuantity,
    installmentDue,
    installmentPeriod,
    formValues?.decreaseAmount,
    formValues?.increaseAmount,
  ]);

  const handleGenerateFinancial = async () => {
    try {
      const values = await form.validateFields();

      const orders = await Promise.all(
        stoneDevices.data?.map((device) =>
          stoneHasOrderOpen.mutateAsync(device.id),
        ) || [],
      );

      if (
        orders.some(
          (orderInfo) =>
            orderInfo.hasOpenOrder &&
            orderInfo.originType === StoneOrderOriginType.SALE &&
            orderInfo.originId === directSale?.id,
        )
      ) {
        message.warn('Existe um processamento da Stone para esta venda direta');

        queryChanel({
          invalidateQueries: [['stoneOrder/hasOpen'], ['stoneDevice/list']],
          refetchQueries: [['stoneOrder/hasOpen'], ['stoneDevice/list']],
        });

        return;
      }

      if (financialType === 'stone') {
        return stoneModalAction.open();
      }

      if (directSale) {
        const obj: AccountReceivableBySaleRequest = {
          ...values,
          saleId: Number(directSaleId),
          companyId: directSale.companyId,
          customerId: directSale.customerId,
          installmentReceivableSales: installments.map((item) => ({
            ...item,
            dueDate: item.dueDate.format(DateTimeFormat),
          })),
        };

        await generateFinancial.mutateAsync(obj);

        setInstallments([]);

        form.resetFields();
      }
    } catch {}
  };

  const tabContens: Record<string, TabItem & { visible: boolean }> = {
    generateFinancial: {
      label: 'Gerar financeiro',
      key: '1',
      visible: totalValues.remainingAmount > 0,
      children: (
        <>
          <FinancialDirectSaleForm
            form={form}
            directSale={directSale}
            totalValues={totalValues}
            isStone={financialType === 'stone'}
            companySetting={companySetting}
            paymentType={documentTypePaymentType}
          />

          <DirectSaleFinancialInstallments
            installments={installments}
            formValues={formValues}
            setInstallments={setInstallments}
            paymentCondition={paymentCondition}
            isStone={financialType === 'stone'}
            companySetting={companySetting}
            documentType={documentTypeDetail.data}
          />

          <DirectSaleFinancialStatistic
            valueTotal={valueTotal}
            installments={installments}
            directSale={directSale}
            accountReceivable={directSaleAccounts}
          />

          <StoneOrderCreateModal
            installments={installments.map((item) => ({
              documentTypeId: item.documentTypeId as number,
              discountIncrease: item.discountIncrease,
              description: item.description,
              documentNumber: item.documentNumber,
              rateValue: item.rateValue || 0,
            }))}
            opened={stoneModalOpened}
            onClose={stoneModalAction.close}
            device={stoneDevice}
            data={{
              paymentType: documentTypePaymentType,
              displayName: `VD - ${directSale?.saleCode}`.slice(0, 17),
              id: directSale?.id as number,
              originType: StoneOrderOriginType.SALE,
              companyId: directSale?.companyId,
              stoneAccountId: stoneAccount.data?.id,
              customerId: directSale?.customerId,
              planAccountId: formValues?.planAccountId,
              documentTypeId: formValues?.documentTypeId,
              items: [
                {
                  description: `Venda Direta - ${directSale?.saleCode}`,
                  quantity: 1,
                  amount: Number(
                    installments
                      .reduce(
                        (acc, item) => acc + (item.installmentValue || 0),
                        0,
                      )
                      .toFixed(2),
                  ),
                },
              ],
            }}
          />
        </>
      ),
    },
    totalValuePendent: {
      label: 'Resumo financeiro',
      key: '2',
      visible: !!directSaleAccounts.length,
      children: (
        <DirectSaleListInstallments
          installments={directSaleAccounts}
          fetchNextPage={fetchNextPage}
          hasNextPage={hasNextPage}
          isFetching={isLoading}
          directSale={directSale}
        />
      ),
    },
  };

  const items = Object.keys(tabContens)
    .map((key) => tabContens[key])
    .filter((item) => !!item.visible);
  const hasItems = !!items.length;

  const [tefOpened, tefAction] = useDisclosure(false, {
    onClose: () => setFinancialType(FinancialType.Default),
  });

  const { isMobile } = useWindowSize();

  const stoneEnabled =
    companySetting?.isInstallmentFree &&
    !!stoneAccount.data?.active &&
    hasPermissions([Authorities.STONE_ORDER_CREATE]);

  const financialTypeOptions = [
    { value: FinancialType.Default, label: 'Padrão', visible: true },
    { value: FinancialType.Stone, label: 'Stone', visible: stoneEnabled },
    { value: FinancialType.Tef, label: 'TEF Elgin', visible: !isMobile },
  ].filter((option) => option.visible);

  const transactionCreate = usePaymentTransactionCreate();

  const handleTefResult = async (result: TefOnResultParams) => {
    if (!directSale) return message.error('Dados da venda inválidos.');

    await transactionCreate.mutateAsync({
      terminalNsu: result.resultado?.tef.nsuTerminal,
      documentTypeId: result.documentTypeId,
      installmentQuantity: result.installments || 1,
      planAccountId: result.planAccountId,
      totalAmount: result.amount,
      acquirerCnpj: result.resultado?.tef.cnpjCredenciadora,
      acquirerCode: result.resultado?.tef.codigoAdquirente,
      code: result.resultado?.tef.sequencial,
      uniqueId: result.resultado?.tef.uniqueID,
      authorizationCode: result.resultado?.tef.codigoAutorizacao,
      cardBrandName: result.resultado?.tef?.nomeBandeira?.toLowerCase(),
      accreditor: TransactionAccreditor.TefElgin,
      clientType: TransactionClientType.Customer,
      entityType: TransactionEntityType.Sale,
      paymentType: TransactionPaymentTypeByTef[result.funcao],
      companyId: directSale.companyId,
      entityId: directSale.id,
      clientId: directSale.customerId,
      entityCode: directSale.saleCode,
    });

    await paymentStatusRef.current?.statusRefetch();
  };

  return (
    <div style={{ position: 'relative' }}>
      <TEFProcessFinancial
        ref={paymentStatusRef}
        enabled={
          directSale?.status === DirectSaleStatusEnum.AUTHORIZED &&
          transactionCreate.isSuccess
        }
        entityId={directSale?.id}
        entityType={TransactionEntityType.Sale}
        refresh={async (status) => {
          if (status === PaymentFinancialPendingStatus.COMPLETED) {
            queryChanel({
              invalidateQueries: [
                [`${QUERY_ACCOUNT_RECEIVABLE}/ListAccountReceivableBySale`],
                [`${QUERY_ACCOUNT_RECEIVABLE}/FinancialPendingBySale`],
                ['direct-sale', directSale?.id],
              ],
              resetQueries: [['dashboard-data']],
            });
            await directSaleFinancialPending.refetch();
            await InstallmentsRefetch();
            transactionCreate.reset();
          }
        }}
      />

      <TEF
        opened={tefOpened}
        onClose={tefAction.close}
        amout={totalValues.remainingAmount}
        onResult={handleTefResult}
        cnpj={company?.cnpj}
      />

      <Skeleton loading={useDirectSaleLoading || isLoading}>
        <Row gutter={[24, 24]}>
          <Col span={24}>
            <SituationFormField initialValue={directSale?.status} />
          </Col>

          <Col span={24}>
            <FinancialTypeGenerate
              tefEnabled={
                directSale?.status === DirectSaleStatusEnum.AUTHORIZED
              }
              companyId={directSale?.companyId}
              visible={
                (directSaleFinancialPending.data?.totalValuePending || 0) > 0
              }
              type={financialType}
              onChange={(value) => {
                form.setFields([
                  { name: 'documentTypeId', value: undefined },
                  { name: 'installmentQuantity', value: 1 },
                ]);

                if (value === 'default') {
                  setStoneDevice(undefined);
                } else if (value === FinancialType.Tef) {
                  setStoneDevice(undefined);
                  tefAction.open();
                }

                documentTypeDetail.reset();
                setFinancialType(value);
              }}
            />
          </Col>

          {financialTypeOptions.length > 1 && (
            <>
              {stoneDevice && (
                <Col>
                  <Popover content={`Dispositivo: ${stoneDevice.serialNumber}`}>
                    <div>
                      <ActionIcon
                        style={{ height: 32 }}
                        onClick={() => setStoneDevice(undefined)}
                      >
                        <img
                          src="/images/device-stone.png"
                          alt="Device Stone"
                          width={24}
                        />
                      </ActionIcon>
                    </div>
                  </Popover>
                </Col>
              )}
            </>
          )}
        </Row>
      </Skeleton>

      <StoneDevicesSelectModal
        open={isStone && !stoneDevice}
        setDevice={setStoneDevice}
        device={stoneDevice}
        stoneAccount={stoneAccount.data}
        onClose={() => {
          if (isStone) {
            setFinancialType(FinancialType.Default);
            setStoneDevice(undefined);
          }
        }}
      />

      {(!isLoading || !useDirectSaleLoading) && (
        <Skeleton loading={isLoading || useDirectSaleLoading} />
      )}

      {!isLoading && hasItems && (
        <div
          style={{ position: 'relative', borderRadius: 12, overflow: 'hidden' }}
        >
          {loadingFinancial && (
            <ModalLoadingOverlay
              messages={['Atualizando dados do financeiro...']}
            />
          )}

          <StoneOrderProcessInfo
            watch={{
              originType: StoneOrderOriginType.SALE,
              companyId: directSale?.companyId,
              originId: directSale?.id,
            }}
            originCode={directSale?.saleCode}
            onFinish={async (type) => {
              if (type === 'order') {
                setLoadingFinancial(true);
                setTimeout(() => {
                  queryChanel({
                    invalidateQueries: [
                      ['accountReceivable/ListAccountReceivableBySale'],
                      ['accountReceivable/FinancialPendingBySale'],
                    ],
                    removeQueries: [
                      ['accountReceivable/ListAccountReceivableBySale'],
                      ['accountReceivable/FinancialPendingBySale'],
                    ],
                  });

                  setLoadingFinancial(false);
                }, 2000);
              }

              setFinancialType(FinancialType.Default);
              setStoneDevice(undefined);
              stoneModalAction.close();
            }}
          />

          <Tabs
            items={items}
            tabBarExtraContent={
              <SpaceForButton>
                <Button
                  type="ghost"
                  onClick={() => {
                    form.resetFields([
                      'planAccountId',
                      'documentTypeId',
                      'paymentConditionId',
                    ]);

                    setInstallments([]);
                  }}
                  disabled={totalValues.totalIsEqual || isCanceled}
                >
                  Desfazer
                </Button>

                <SaveButton
                  disabled={
                    totalValues.totalIsEqual ||
                    directSale?.status !== DirectSaleStatusEnum.AUTHORIZED ||
                    !hasPermissions([
                      Authorities.MOVIMENTATION_SALE_GENERATE_FINANCIAL,
                    ]) ||
                    (financialType === 'stone' &&
                      (hasMax12InstallmentAndLower10 ||
                        hasOver12InstallmentAndLower10))
                  }
                  block
                  onClick={handleGenerateFinancial}
                  loading={generateFinancial.isLoading}
                >
                  {intl.formatMessage({
                    id:
                      financialType === 'stone'
                        ? 'directSale.financial.table.generateOrder'
                        : 'directSale.financial.table.generateFinancial',
                  })}
                </SaveButton>
              </SpaceForButton>
            }
          />
        </div>
      )}

      {!hasItems && (
        <Empty
          description="Não há dados para gerar financeiro"
          style={{ margin: '100px 0' }}
        />
      )}

      <Divider />

      <SpaceForButton>
        <ButtonBack path="/app/movements/directSale" />
      </SpaceForButton>
    </div>
  );
};

export default DirectSaleFinancialTabs;
